<template>
    <div class="thread-timeline">
        <div
            v-for="(entry, index) in entries"
            :key="entry.id"
            class="timeline-entry"
            :class="`entry-${entry.type}`"
        >
            <!-- Proposal reply (via the Confirm/Cancel buttons) -->
            <div
                v-if="proposalReplyKind(entry, index)"
                class="proposal-reply-row"
                :class="proposalReplyKind(entry, index)"
            >
                <i
                    :class="
                        proposalReplyKind(entry, index) === 'confirmed'
                            ? 'pi pi-check-circle'
                            : 'pi pi-times-circle'
                    "
                />
                <span class="proposal-reply-text">
                    {{ proposalReplyLabel(entry, index) }}
                </span>
                <span class="timestamp">{{ formatTime(entry.timestamp) }}</span>
            </div>

            <!-- Contribution (user or external agent) -->
            <div
                v-else-if="entry.type === 'contribution'"
                class="entry-bubble contribution-bubble"
            >
                <div class="bubble-header">
                    <span class="source-name">{{ entry.source.name }}</span>
                    <Tag
                        v-if="entry.source.type === 'external_agent'"
                        value="external agent"
                        severity="secondary"
                        class="source-tag"
                    />
                    <span class="timestamp">{{
                        formatTime(entry.timestamp)
                    }}</span>
                </div>
                <div
                    class="bubble-content"
                    v-html="md.render(entry.content ?? '')"
                ></div>
                <div v-if="entry.references?.length" class="references">
                    <div
                        v-for="(ref, i) in entry.references"
                        :key="i"
                        class="reference-chip"
                    >
                        <span v-if="ref.type === 'entity'" class="ref-entity">
                            {{ ref.entity_type }}: {{ ref.entity_name }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Agent action (activity log item — silent, not a chat message) -->
            <div
                v-else-if="entry.type === 'agent_action'"
                class="agent-action-row"
                :class="
                    entry.action_detail?.success === false
                        ? 'action-failed'
                        : ''
                "
            >
                <i
                    :class="[
                        'action-icon',
                        actionIcon(entry.action_detail?.action_type),
                    ]"
                />
                <span class="action-detail">{{
                    entry.action_detail?.detail ?? entry.content
                }}</span>
                <span class="timestamp">{{ formatTime(entry.timestamp) }}</span>
            </div>

            <!-- System event (activity feed only) -->
            <div
                v-else-if="entry.type === 'system_event'"
                class="agent-action-row"
            >
                <i class="action-icon pi pi-info-circle" />
                <span class="action-detail">{{ entry.content }}</span>
                <span class="timestamp">{{ formatTime(entry.timestamp) }}</span>
            </div>

            <!-- Agent response -->
            <div
                v-else-if="entry.type === 'agent_response'"
                class="entry-bubble agent-bubble"
            >
                <div class="bubble-header">
                    <span class="source-name">{{ entry.source.name }}</span>
                    <span class="timestamp">{{
                        formatTime(entry.timestamp)
                    }}</span>
                </div>
                <div
                    class="bubble-content"
                    v-html="md.render(stripAgentEnvelope(entry.content ?? ''))"
                ></div>
                <div v-if="entry.references?.length" class="references">
                    <div
                        v-for="(ref, i) in entry.references"
                        :key="i"
                        class="reference-chip"
                    >
                        <span v-if="ref.type === 'entity'" class="ref-entity">
                            {{ ref.entity_type }}: {{ ref.entity_name }}
                        </span>
                        <span
                            v-else-if="ref.type === 'thread'"
                            class="ref-thread"
                        >
                            Thread: {{ ref.title }}
                            <Tag
                                :value="ref.status ?? ''"
                                severity="secondary"
                            />
                        </span>
                    </div>
                </div>
                <div
                    v-if="showApprovalButtons(entry, index)"
                    class="approval-actions"
                >
                    <Button
                        label="Confirm"
                        icon="pi pi-check"
                        severity="success"
                        outlined
                        size="small"
                        @click="emit('confirm', entry.id)"
                    />
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        severity="danger"
                        outlined
                        size="small"
                        @click="emit('cancel', entry.id)"
                    />
                </div>
            </div>

            <!-- Resolution -->
            <div
                v-else-if="entry.type === 'resolution'"
                class="entry-resolution"
            >
                <div
                    class="resolution-banner"
                    :class="
                        entry.content?.includes('accept')
                            ? 'accepted'
                            : 'rejected'
                    "
                >
                    <i
                        :class="
                            entry.content?.includes('accept')
                                ? 'pi pi-check-circle'
                                : 'pi pi-times-circle'
                        "
                    />
                    <div class="resolution-details">
                        <span class="resolution-action">{{
                            entry.content
                        }}</span>
                        <span class="resolution-by"
                            >by {{ entry.source.name }}</span
                        >
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import MarkdownIt from "markdown-it";
import Button from "primevue/button";
import Tag from "primevue/tag";

import {
    PROPOSAL_CANCEL_MESSAGE,
    PROPOSAL_CONFIRM_MESSAGE,
    stripAgentEnvelope,
} from "@/components/Thread/thread-content";
import type { ThreadEntrySchema } from "@/stores/threads-store";
import { useUserStore } from "@/stores/user-store";

const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

const props = defineProps<{
    entries: ThreadEntrySchema[];
    threadOpen?: boolean;
}>();

const emit = defineEmits<{
    confirm: [entryId: string];
    cancel: [entryId: string];
}>();

// Only on a still-live proposal — stale once anything follows or the thread closes.
const showApprovalButtons = (entry: ThreadEntrySchema, index: number) =>
    props.threadOpen === true &&
    entry.type === "agent_response" &&
    entry.message_reason === "approval_request" &&
    index === props.entries.length - 1;

// Button-posted reply to the proposal right before it; hand-typed replies stay bubbles.
const proposalReplyKind = (
    entry: ThreadEntrySchema,
    index: number
): "confirmed" | "cancelled" | null => {
    if (entry.type !== "contribution") return null;
    const prev = props.entries[index - 1];
    if (
        !prev ||
        prev.type !== "agent_response" ||
        prev.message_reason !== "approval_request"
    ) {
        return null;
    }
    if (entry.content === PROPOSAL_CONFIRM_MESSAGE) return "confirmed";
    if (entry.content === PROPOSAL_CANCEL_MESSAGE) return "cancelled";
    return null;
};

const userStore = useUserStore();

const proposalReplyLabel = (entry: ThreadEntrySchema, index: number) => {
    const kind = proposalReplyKind(entry, index);
    const own = entry.source.id === userStore.user?.identity;
    if (kind === "confirmed") {
        return own ? "You confirmed this" : `Confirmed by ${entry.source.name}`;
    }
    return own ? "You cancelled this" : `Cancelled by ${entry.source.name}`;
};

const actionIcon = (actionType?: string | null) => {
    if (!actionType) return "pi pi-bolt";
    if (actionType.startsWith("create")) return "pi pi-plus-circle";
    if (actionType.startsWith("delete")) return "pi pi-minus-circle";
    if (actionType.startsWith("update")) return "pi pi-pencil";
    return "pi pi-bolt";
};

const formatTime = (timestamp: string | Date) => {
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;

    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};
</script>

<style scoped>
.thread-timeline {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.entry-bubble {
    border-radius: 12px;
    padding: 0.75rem 1rem;
    max-width: 85%;
}

.contribution-bubble {
    background: var(--p-surface-100);
    align-self: flex-start;
}

.agent-bubble {
    background: var(--p-primary-50);
    border: 1px solid var(--p-primary-100);
    align-self: flex-start;
}

.bubble-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
}

.source-name {
    font-weight: 600;
    font-size: 0.85rem;
}

.source-tag {
    font-size: 0.7rem;
}

.timestamp {
    font-size: 0.75rem;
    color: var(--p-text-muted-color);
    margin-left: auto;
}

.bubble-content {
    font-size: 0.95rem;

    :deep(p) {
        margin: 0.25em 0;
    }

    :deep(p:first-child) {
        margin-top: 0;
    }

    :deep(p:last-child) {
        margin-bottom: 0;
    }

    :deep(a) {
        color: var(--p-primary-color);
        text-decoration: underline;
    }

    :deep(code) {
        background: var(--p-surface-200);
        padding: 0.1em 0.3em;
        border-radius: 3px;
        font-size: 0.85em;
    }

    :deep(ul),
    :deep(ol) {
        margin: 0.25em 0;
        padding-left: 1.5em;
    }
}

/* Wrap long/unbreakable code content instead of overflowing the narrow bubble.
   Written as top-level scoped selectors: the nested `.bubble-content { :deep() }`
   form above does not compile to a working selector in this build. */
.bubble-content :deep(pre) {
    max-width: 100%;
    margin: 0.25em 0;
    padding: 0.5em 0.75em;
    background: var(--p-surface-200);
    border-radius: 6px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}

.bubble-content :deep(pre) code {
    background: none;
    padding: 0;
}

.bubble-content :deep(code) {
    overflow-wrap: anywhere;
}

.references {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.approval-actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.75rem;
}

.proposal-reply-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.75rem;
    font-size: 0.85rem;
    font-weight: 500;
}

.proposal-reply-row.confirmed {
    border-left: 2px solid var(--p-green-300);
    color: var(--p-green-700);
}

.proposal-reply-row.cancelled {
    border-left: 2px solid var(--p-red-300);
    color: var(--p-red-600);
}

.proposal-reply-text {
    flex: 1;
}

.reference-chip {
    background: var(--p-surface-0);
    border: 1px solid var(--p-surface-200);
    border-radius: 6px;
    padding: 0.25rem 0.5rem;
    font-size: 0.8rem;
}

.agent-action-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.75rem;
    border-left: 2px solid var(--p-primary-200);
    font-size: 0.85rem;
    color: var(--p-text-muted-color);
}

.agent-action-row.action-failed {
    border-left-color: var(--p-red-300);
    color: var(--p-red-600);
}

.action-icon {
    font-size: 0.8rem;
    flex-shrink: 0;
}

.agent-action-row:not(.action-failed) .action-icon {
    color: var(--p-primary-400);
}

.action-detail {
    flex: 1;
}

.resolution-banner {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    font-weight: 500;
}

.resolution-banner.accepted {
    background: var(--p-green-50);
    border: 1px solid var(--p-green-200);
    color: var(--p-green-700);
}

.resolution-banner.rejected {
    background: var(--p-red-50);
    border: 1px solid var(--p-red-200);
    color: var(--p-red-700);
}

.resolution-banner i {
    font-size: 1.25rem;
}

.resolution-details {
    display: flex;
    flex-direction: column;
}

.resolution-action {
    font-size: 0.95rem;
}

.resolution-by {
    font-size: 0.8rem;
    opacity: 0.8;
}
</style>
